import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './StudioEdit.css';
import { API_CONFIG } from '../../config/api';

export const StudioEdit = () => {
  function getCookie(name) {
    let cookieArray = document.cookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookiePair = cookieArray[i].trim().split('=');
      if (cookiePair[0] === name) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }

  const { tourId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    artist_id: '',
    tour_name: '',
    tour_profile: null,
    tour_hashtag: '',
    tour_description: '',
    tour_phone: '',
    tour_website: '',
    tour_location: '',
    tour_location_do: '',
    tour_location_si: '',
    tour_location_lat: '',
    tour_location_lng: '',
    tour_type: 'workshop',
    tour_price: '',
    tour_open_days: [],
    tour_open_time: '10:00',
    tour_close_time: '18:00',
    tour_break_time: '12:00-13:00',
    tour_max_per_time: 20
  });

  useEffect(() => {
    const artistId = getCookie('yournest_a_id');
    if (!artistId) {
      alert('로그인이 필요한 서비스입니다.');
      navigate('/login');
      return;
    }
    fetchTourData();
    setFormData(prev => ({ ...prev, artist_id: artistId }));

  }, [tourId]);

  useEffect(() => {
    const loadKakaoScript = async () => {
      try {
        await new Promise((resolve, reject) => {
          const postScript = document.createElement("script");
          postScript.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
          postScript.onload = resolve;
          postScript.onerror = reject;
          document.head.appendChild(postScript);
        });

        await new Promise((resolve, reject) => {
          const mapScript = document.createElement("script");
          mapScript.src = "//dapi.kakao.com/v2/maps/sdk.js?appkey=cdabbe0b4373d904f58d672d596b6630&libraries=services&autoload=false";
          mapScript.onload = () => {
            window.kakao.maps.load(() => {
              console.log("Kakao maps loaded with services");
              resolve();
            });
          };
          mapScript.onerror = reject;
          document.head.appendChild(mapScript);
        });
      } catch (error) {
        console.error('스크립트 로드 실패:', error);
        alert('지도 서비스 로드에 실패했습니다.');
      }
    };

    loadKakaoScript();
  }, []);

  const fetchTourData = async () => {
    try {
      const response = await fetch(`${API_CONFIG.MAIN}/get_tour_overview_by_id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tour_id: tourId })
      });

      if (!response.ok) throw new Error('투어 데이터 가져오기 실패');
      
      const data = await response.json();
      if (data.isSuccess) {
        try {
          const parsedOpenDays = data.tour.tour_open_days ? 
            (typeof data.tour.tour_open_days === 'string' ? 
              JSON.parse(data.tour.tour_open_days) : 
              data.tour.tour_open_days) : 
            [];

          setFormData(prev => ({
            ...data.tour,
            tour_open_days: parsedOpenDays,
            existing_profile: data.tour.tour_profile ? 
              `${API_CONFIG.MAIN}/uploads/tour_profiles/${data.tour.tour_profile}` : null,
            tour_profile: null
          }));
        } catch (parseError) {
          console.error('tour_open_days 파싱 오류:', parseError);
          setFormData(prev => ({
            ...data.tour,
            tour_open_days: [],
            existing_profile: data.tour.tour_profile ? 
              `${API_CONFIG.MAIN}/uploads/tour_profiles/${data.tour.tour_profile}` : null,
            tour_profile: null
          }));
        }
      }
    } catch (error) {
      console.error('투어 데이터 가져오기 실패:', error);
      alert('데이터를 불러오는데 실패했습니다.');
      navigate('/artist-studio');
    }
  };

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        const addr = data.address;
        const sido = data.sido;
        const sigungu = data.sigungu;
        
        setFormData(prev => ({
          ...prev,
          tour_location: addr,
          tour_location_do: sido,
          tour_location_si: sigungu
        }));
        
        const geocoder = new window.kakao.maps.services.Geocoder();
        geocoder.addressSearch(addr, function(result, status) {
          if (status === window.kakao.maps.services.Status.OK) {
            setFormData(prev => ({
              ...prev,
              tour_location_lat: result[0].y,
              tour_location_lng: result[0].x
            }));
          } else {
            alert("주소를 좌표로 변환하는데 실패했습니다. 다시 시도해주세요.");
          }
        });
      }
    }).open();
  };

  const DAY_TO_NUMBER = {
    '일': 0,
    '월': 1,
    '화': 2,
    '수': 3,
    '목': 4,
    '금': 5,
    '토': 6
  };

  const handleDayToggle = (day) => {
    const dayNumber = DAY_TO_NUMBER[day];
    setFormData(prev => ({
      ...prev,
      tour_open_days: prev.tour_open_days.includes(dayNumber)
        ? prev.tour_open_days.filter(d => d !== dayNumber)
        : [...prev.tour_open_days, dayNumber].sort((a, b) => a - b)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tour_location_lat || !formData.tour_location_lng) {
      alert('유효한 주소를 입력해주세요.');
      return;
    }

    try {
      let profileFilename = formData.tour_profile ? null : formData.existing_profile?.split('/').pop();
      
      if (formData.tour_profile) {
        const imageFormData = new FormData();
        imageFormData.append('tour_profile', formData.tour_profile);
        
        const uploadResponse = await fetch(`${API_CONFIG.MAIN}/upload_tour_profile`, {
          method: 'POST',
          body: imageFormData
        });

        if (!uploadResponse.ok) throw new Error('이미지 업로드 실패');
        const uploadResult = await uploadResponse.json();
        profileFilename = uploadResult.filename;
      }

      const response = await fetch(`${API_CONFIG.MAIN}/update_tour`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          tour_id: tourId,
          tour_profile: profileFilename,
          tour_open_days: formData.tour_open_days.join(',')
        })
      });

      if (!response.ok) throw new Error('아틀리에 수정 실패');
      
      alert('아틀리에가 수정되었습니다.');
      navigate('/artist-studio');
    } catch (error) {
      console.error('아틀리에 수정 실패:', error);
      alert('아틀리에 수정에 실패했습니다.');
    }
  };

  return (
    <div className="studio-edit">
      <div className="studio-edit__form">
        <h2>아틀리에 정보 수정</h2>
        <div className="studio-edit__input-group">
          <label>아틀리에 이름 *</label>
          <input
            type="text"
            value={formData.tour_name}
            onChange={e => setFormData({...formData, tour_name: e.target.value})}
            required
          />
        </div>
        <div className="studio-edit__input-group">
          <label>해시태그</label>
          <input
            type="text"
            value={formData.tour_hashtag}
            onChange={e => setFormData({...formData, tour_hashtag: e.target.value})}
            placeholder="#공방 #도예 #클래스"
          />
        </div>
        <div className="studio-edit__input-group">
          <label>아틀리에 설명 *</label>
          <textarea
            value={formData.tour_description}
            onChange={e => setFormData({...formData, tour_description: e.target.value})}
            required
          />
        </div>
        <div className="studio-edit__input-group">
          <label>주소 *</label>
          <div className="studio-edit__address-input">
            <input
              type="text"
              value={formData.tour_location}
              readOnly
              required
            />
            <button type="button" onClick={handleAddressSearch}>
              주소 검색
            </button>
          </div>
        </div>
        <div className="studio-edit__input-group">
          <label>아틀리에 유형 *</label>
          <select
            value={formData.tour_type}
            onChange={e => setFormData({...formData, tour_type: e.target.value})}
            required
          >
            <option value="workshop">아틀리에</option>
            <option value="experience">아틀리에</option>
            <option value="class">레지던시</option>
          </select>
        </div>
        <div className="studio-edit__input-group">
          <label>가격 *</label>
          <input
            type="number"
            value={formData.tour_price}
            onChange={e => setFormData({...formData, tour_price: e.target.value})}
              required
          />
        </div>
        <div className="studio-edit__input-group">
          <label>운영 요일</label>
          <div className="studio-edit__days-selector">
            {['일', '월', '화', '수', '목', '금', '토'].map(day => (
              <button
                key={day}
                type="button"
                className={formData.tour_open_days.includes(DAY_TO_NUMBER[day]) ? 'studio-edit__active' : 'studio-edit__inactive'}
                onClick={() => handleDayToggle(day)}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
        <div className="studio-edit__input-group">
          <label>운영 시간 *</label>
          <div className="studio-edit__time-inputs">
            <input
              type="time"
              value={formData.tour_open_time}
              onChange={e => setFormData({...formData, tour_open_time: e.target.value})}
              required
            />
            <span>~</span>
            <input
              type="time"
              value={formData.tour_close_time}
              onChange={e => setFormData({...formData, tour_close_time: e.target.value})}
              required
            />
          </div>
        </div>
        <div className="studio-edit__input-group">
          <label>브레이크타임</label>
          <input
            type="text"
            value={formData.tour_break_time}
            onChange={e => setFormData({...formData, tour_break_time: e.target.value})}
            placeholder="12:00-13:00"
          />
        </div>
        <div className="studio-edit__input-group">
          <label>최대 인원 *</label>
          <input
            type="number"
            value={formData.tour_max_per_time}
            onChange={e => setFormData({...formData, tour_max_per_time: e.target.value})}
            required
          />
        </div>
        <div className="studio-edit__input-group">
          <label>전화번호 *</label>
          <input
            type="tel"
            value={formData.tour_phone}
            onChange={e => setFormData({...formData, tour_phone: e.target.value})}
            required
          />
        </div>
        <div className="studio-edit__input-group">
          <label>웹사이트</label>
          <input
            type="url"
            value={formData.tour_website}
            onChange={e => setFormData({...formData, tour_website: e.target.value})}
          />
        </div>
        <div className="studio-edit__input-group">
          <label className="studio-edit__label">대표 이미지</label>
          {formData.existing_profile && (
            <div className="studio-edit__current-image">
              <img 
                src={formData.existing_profile} 
                alt="현재 대표 이미지" 
                className="preview-image"
              />
              <button 
                className="studio-edit__remove-image"
                onClick={() => setFormData({
                  ...formData, 
                  existing_profile: null,
                  tour_profile: null
                })}
              >
                ✕
              </button>
            </div>
          )}
          <label className="studio-edit__file-input-label">
            이미지 {formData.existing_profile ? '변경' : '추가'}하기
            <input
              type="file"
              onChange={e => setFormData({...formData, tour_profile: e.target.files[0]})}
              accept="image/*"
              className="studio-edit__file-input"
            />
          </label>
          {formData.tour_profile && (
            <span className="studio-edit__file-name">
              {formData.tour_profile.name}
            </span>
          )}
        </div>

        <div className="studio-edit__button-group">
          <div className="studio-edit__submit-button" onClick={handleSubmit}>수정하기</div>
          <div className="studio-edit__cancel-button" onClick={() => navigate('/artist-studio')}>취소</div>
        </div>
      </div>
    </div>
  );
};
